import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import useSmartBack from '@hooks/shared/useSmartBack';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import SalesChannelFormConceptModal from '@components/shared/sales_channels/SalesChannelFormConceptModal';
import PageHeader from '@shared/PageHeader';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Space,
  Card,
  Table,
  message,
  Modal,
  Spin,
  Row,
  Col,
  Divider,
  Tooltip,
  Alert
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const { Option } = Select;

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface ShippingTypeOption {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

interface SalesChannelLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
  [key: string]: unknown;
}

interface SalesChannelsEditProps {
  // role-agnostic shared edit; admin / operator wrappers inject services + reference data.
  role?: 'admin' | 'operator';
  isNew?: boolean;
  // services 物件方法多樣（create/update/get/fetchList），保留 any 與其他 shared edit 一致。
  salesChannelService: any;
  shippingTypeService?: any;
  inventoryTypeService?: any;
  // merchant-scoped reference data — wrapper 負責對齊 loaded record 的 merchant
  inventoryTypes?: InventoryTypeOption[];
  shippingTypes?: ShippingTypeOption[];
  // 新增模式建立通路所屬 merchant id（operator 才需要傳）
  merchantIdForCreate?: string | null;
  // Wrapper 透過此 callback 拿到 loaded record，再用 useRecordMerchantSync 對齊 context
  onSalesChannelLoaded?: (channel: SalesChannelLoadedPayload) => void;
  // wrapper 還在切 currentMerchant 時為 true，render-gate Form 避免閃錯選項
  merchantDataLoading?: boolean;
}

/**
 * SalesChannelsEdit - 通用銷售通路編輯/新增組件
 *
 * Merchant-scope 修正後：shared 本身不再 useOperatorData()。所有 merchant-scoped
 * reference data 由 wrapper 注入；wrapper 用 useRecordMerchantSync 把 OperatorDataContext
 * 對齊到 loaded record 的 merchant，避免 multi-merchant operator 切到 B 後打開 A 的通路
 * 時下拉顯示 B 的 inventory_type / shipping_type 並把 B 的 id 寫回 A 的記錄。
 */
const SalesChannelsEdit: React.FC<SalesChannelsEditProps> = ({
  role = 'admin',
  isNew = false,
  salesChannelService,
  shippingTypeService,
  inventoryTypeService,
  inventoryTypes = [],
  shippingTypes: shippingTypesProp,
  merchantIdForCreate = null,
  onSalesChannelLoaded,
  merchantDataLoading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { id } = useParams<{ id?: string }>();
  const [form] = Form.useForm();
  const isOperator = role === 'operator';
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [salesChannel, setSalesChannel] = useState<Record<string, any> | null>(null);
  const [rules, setRules] = useState<Array<Record<string, any>>>([]);
  const [shippingRules, setShippingRules] = useState<Array<Record<string, any>>>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialDataRef = useRef<Record<string, any> | null>(null);

  // shippingTypes 預設來自 props；若 wrapper 沒注入（理論上不該發生），退回 service 直接 fetch。
  const [shippingTypesData, setShippingTypesData] = useState<ShippingTypeOption[]>(shippingTypesProp ?? []);
  const shippingTypes = shippingTypesProp ?? shippingTypesData;

  // 同步 props → state（wrapper 重 fetch 後讓 shared 看到新資料）
  useEffect(() => {
    if (shippingTypesProp) setShippingTypesData(shippingTypesProp);
  }, [shippingTypesProp]);

  const [conceptModalVisible, setConceptModalVisible] = useState(false);
  const saveRef = useRef<HTMLButtonElement | null>(null);

  // Fallback：若 wrapper 沒注入 shippingTypes，使用 shippingTypeService 載入（保留 admin 舊行為）
  useEffect(() => {
    if (shippingTypesProp !== undefined) return;
    if (!shippingTypeService) return;
    setOptionsLoading(true);
    shippingTypeService
      .index()
      .then((res: { success?: boolean; data?: ShippingTypeOption[] }) => {
        if (res?.success && Array.isArray(res.data)) {
          setShippingTypesData(res.data);
        }
      })
      .catch((err: unknown) => {
        console.error('Load shipping types error:', err);
      })
      .finally(() => setOptionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 載入 sales_channel 資料（僅編輯模式）
  useEffect(() => {
    if (id && !isNew) {
      loadSalesChannel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const loadSalesChannel = useCallback(async () => {
    setLoading(true);
    try {
      const result = await salesChannelService.get(id);
      if (result.success) {
        const data = result.data;
        setSalesChannel(data);

        // 設置表單值
        form.setFieldsValue({
          ...data,
          sales_channel_rules: data.sales_channel_rules || [],
          sales_channel_shipping_rules: data.sales_channel_shipping_rules || [],
          channel_type: data.channel_type || 'normal',
        });

        // 設置規則
        setRules(data.sales_channel_rules || []);
        setShippingRules(data.sales_channel_shipping_rules || []);

        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant 對齊到該
        // record 的 merchant（由 useRecordMerchantSync 處理）。shared 本身不再讀 / 寫
        // merchant context，保持 merchant-agnostic。
        onSalesChannelLoaded?.(data as SalesChannelLoadedPayload);

        // 保存初始資料用於比較
        initialDataRef.current = {
          formData: form.getFieldsValue(),
          rules: data.sales_channel_rules || [],
          shippingRules: data.sales_channel_shipping_rules || [],
        };
      } else {
        message.error(result.message || t('common.error.load_failed'));
      }
    } catch (error) {
      console.error('Load merchant channel error:', error);
      message.error(t('common.error.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [id, form, t, salesChannelService, onSalesChannelLoaded]);

  // 組出 API payload，驗證規則重複則回傳 null
  const buildPayload = (values: Record<string, any>): Record<string, any> | null => {
    const payload: Record<string, any> = {
      sales_channel: {
        name: values.name,
        extra_name: values.extra_name,
        enabled: values.enabled,
        map_product_rule: values.map_product_rule,
        inventory_type_id: values.inventory_type_id,
        secondary_inventory_type_id: values.secondary_inventory_type_id,
        allow_partial_allocation: values.allow_partial_allocation ?? null,
        fulfillment_lot_policy: values.fulfillment_lot_policy || null,
        auto_complete_fulfillment: values.auto_complete_fulfillment,
        auto_reallocate_orders: values.auto_reallocate_orders,
        auto_reallocate_orders_priority: values.auto_reallocate_orders_priority,
        prioritize_full_box_allocation: values.prioritize_full_box_allocation,
        same_expiration_date: values.same_expiration_date,
        expiration_mechanism: values.expiration_mechanism,
        days_before_expiration: values.days_before_expiration,
        expiration_ratio: values.expiration_ratio,
        oversea_days_before_expiration: values.oversea_days_before_expiration,
        oversea_expiration_ratio: values.oversea_expiration_ratio,
        enable_last_shipped_expiration_date_check: values.enable_last_shipped_expiration_date_check,
        check_fee: values.check_fee,
        picking_fee_per_item: values.picking_fee_per_item,
        picking_fee_box: values.picking_fee_box,
        picking_fee_pallet: values.picking_fee_pallet,
      },
    };

    // 如果有規則，添加到 payload
    if (rules.length > 0) {
      // 驗證規則門檻值不重複
      const thresholds = rules.map(r => r.original_expiration_date_threshold);
      const uniqueThresholds = [...new Set(thresholds)];

      if (thresholds.length !== uniqueThresholds.length) {
        message.error(t('shared.sales_channels.form.validation.duplicate_threshold') || '階梯式規則的門檻值不可重複');
        return null;
      }

      const sortedRules = [...rules].sort((a, b) =>
        (a.original_expiration_date_threshold || 0) - (b.original_expiration_date_threshold || 0)
      );

      payload.sales_channel_rules = sortedRules;
    }

    if (shippingRules.length > 0) {
      payload.sales_channel_shipping_rules = shippingRules;
    }

    return payload;
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 result
  // 傳整個 payload（含 top-level sales_channel_rules / sales_channel_shipping_rules），
  // 不可只傳 payload.sales_channel，否則階梯式效期與自動轉換物流規則會被丟掉、後端收不到。
  const submitPayload = async (payload: Record<string, any>) => {
    if (isNew) {
      // 新增模式：Operator 需要傳遞 merchant_id（由 wrapper 從 currentMerchant 取得後傳入）
      const merchantIdParam = role === 'operator' ? merchantIdForCreate : undefined;
      return await salesChannelService.create(payload, merchantIdParam);
    }
    // 編輯模式
    return await salesChannelService.update(id, payload);
  };

  // 處理表單提交：成功後導向 show 頁
  const handleSubmit = useCallback(async (values: Record<string, any>) => {
    const payload = buildPayload(values);
    if (!payload) return;

    setSubmitting(true);
    try {
      const result = await submitPayload(payload);

      if (result.success) {
        message.success(t(isNew ? 'common.message.create_success' : 'common.message.update_success'));
        setHasUnsavedChanges(false);
        const newId = result.data?.id || id;
        const basePath = role === 'operator' ? '/data/sales_channels' : '/sales_channels';
        navigate(`${basePath}/${newId}`, { replace: true });
      } else {
        message.error(result.message || t(isNew ? 'common.error.create_failed' : 'common.error.update_failed'));
      }
    } catch (error) {
      console.error(isNew ? 'Create merchant channel error:' : 'Update merchant channel error:', error);
      message.error(t(isNew ? 'common.error.create_failed' : 'common.error.update_failed'));
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, rules, shippingRules, navigate, t, salesChannelService, merchantIdForCreate, role]);

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單 + 規則 state
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      if (!payload) return;

      setSavingAndAdding(true);
      try {
        const result = await submitPayload(payload);

        if (result.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
          setRules([]);
          setShippingRules([]);
          setHasUnsavedChanges(false);
        } else {
          message.error(result.message || t('common.error.create_failed'));
        }
      } catch (error) {
        console.error('Create merchant channel error:', error);
        message.error(t('common.error.create_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // 新增規則
  const handleAddRule = useCallback(() => {
    const newRule = {
      id: `new_${Date.now()}`,
      original_expiration_date_threshold: 0,
      days_before_expiration: 0,
      expiration_ratio: 0,
      oversea_days_before_expiration: 0,
      oversea_expiration_ratio: 0,
    };
    setRules([...rules, newRule]);
  }, [rules]);

  // 更新規則（含驗證和自動排序）
  const handleUpdateRule = useCallback((index: number, field: string, value: any) => {
    const newRules = [...rules];
    newRules[index] = {
      ...newRules[index],
      [field]: value,
    };

    if (field === 'original_expiration_date_threshold') {
      newRules.sort((a, b) => (a.original_expiration_date_threshold || 0) - (b.original_expiration_date_threshold || 0));

      const thresholds = newRules.map(r => r.original_expiration_date_threshold);
      const hasDuplicates = thresholds.some((val, idx) => thresholds.indexOf(val) !== idx);

      if (hasDuplicates && value > 0) {
        message.warning(t('shared.sales_channels.form.validation.duplicate_threshold') || '門檻值不可重複');
      }
    }

    setRules(newRules);
  }, [rules, t]);

  const handleDeleteRule = useCallback((index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    setRules(newRules);
  }, [rules]);

  const handleAddShippingRule = useCallback(() => {
    const newRule = {
      id: `new_${Date.now()}`,
      original_shipping_type_id: null,
      shipping_type_id: null,
      condition: 'volume',
      criteria: { smaller_than: 0 },
    };
    setShippingRules([...shippingRules, newRule]);
    setHasUnsavedChanges(true);
  }, [shippingRules]);

  const handleUpdateShippingRule = useCallback((index: number, field: string, value: any) => {
    const newRules = [...shippingRules];
    const rule = { ...newRules[index] };

    if (field === 'condition') {
      rule.condition = value;
      if (['volume', 'weight'].includes(value)) {
        rule.criteria = { smaller_than: 0 };
      } else if (value === 'postal_code') {
        rule.criteria = { contains: [] };
      }
    } else if (field === 'operator') {
      const currentValue = Object.values(rule.criteria)[0];
      rule.criteria = { [value]: currentValue };
    } else if (field === 'operand') {
      const currentOperator = Object.keys(rule.criteria)[0];
      rule.criteria = { [currentOperator]: value };
    } else {
      rule[field] = value;
    }

    newRules[index] = rule;
    setShippingRules(newRules);
    setHasUnsavedChanges(true);
  }, [shippingRules]);

  const handleDeleteShippingRule = useCallback((index: number) => {
    const newRules = shippingRules.filter((_, i) => i !== index);
    setShippingRules(newRules);
    setHasUnsavedChanges(true);
  }, [shippingRules]);

  // 監聽效期機制變更
  const handleExpirationMechanismChange = (value: string) => {
    form.setFieldValue('expiration_mechanism', value);
    setHasUnsavedChanges(true);
  };

  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleGoBack = useCallback(() => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: t('common.confirm.unsaved_title') || '未儲存的變更',
        content: t('common.confirm.unsaved_content') || '您有未儲存的變更，確定要離開嗎？',
        okText: t('common.button.leave') || '離開',
        cancelText: t('common.button.stay') || '留下',
        onOk: () => {
          goBack();
        },
      });
    } else {
      goBack();
    }
  }, [hasUnsavedChanges, goBack, t]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const expirationMechanism = Form.useWatch('expiration_mechanism', form);
  const mapProductRule = Form.useWatch('map_product_rule', form);
  const autoRecheckStock = Form.useWatch('auto_reallocate_orders', form);

  const basePath = isOperator ? '/operator' : '/admin';
  const breadcrumbItems = isOperator
    ? ['sales_channels', { label: isNew ? t('shared.sales_channels.form.new_title') : t('shared.sales_channels.form.edit_title') }]
    : ['data', 'data.sales_channels', { label: isNew ? t('shared.sales_channels.form.new_title') : t('shared.sales_channels.form.edit_title') }];

  // Render-gate: sync 期間（optionsLoading 抓 order_templates / shipping_types；
  // loading 抓 record；merchantDataLoading 切 context 到 record 的 merchant）整段
  // form 不能 mount，避免 shipping_type / order_template 下拉先用 header merchant 的
  // 清單 render。
  if (optionsLoading || (loading && !isNew) || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        basePath={basePath}
        items={breadcrumbItems}
        guideButton={
          <Tooltip title={t('concepts.sales_channels_form.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleOutlined style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        initialValues={{
          enabled: true,
          map_product_rule: 'sku',
          allow_partial_allocation: null,
          fulfillment_lot_policy: null,
          auto_complete_fulfillment: false,
          auto_reallocate_orders: false,
          auto_reallocate_orders_priority: 0,
          prioritize_full_box_allocation: false,
          same_expiration_date: false,
          expiration_mechanism: 'none',
          enable_last_shipped_expiration_date_check: false,
        }}
      >
        {/* 基本資訊 */}
        <Card
          title={<Space><InfoCircleOutlined />{t('shared.sales_channels.form.sections.basic_info')}</Space>}
          style={{ marginBottom: 16 }}
        >
            <Row gutter={16}>
              <Col span={12}>
                  <Form.Item
                    name="name"
                    label={t('models.sales_channel.name')}
                    rules={[{ required: true, message: t('common.validation.required') }]}
                  >
                    <Input placeholder={t('common.placeholder.please_enter')} />
                  </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="extra_name"
                  label={t('models.sales_channel.extra_name')}
                >
                  <Input placeholder={t('common.placeholder.please_enter')} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="enabled"
                  label={t('models.sales_channel.enabled')}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                    name="map_product_rule"
                    label={
                      <Space>
                        {t('models.sales_channel.map_product_rule')}
                        <Tooltip title={t('shared.sales_channels.form.tooltips.map_product_rule')}>
                          <QuestionCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                      </Space>
                    }
                  >
                    <Select
                      placeholder={t('common.placeholder.please_select')}
                      allowClear
                      optionLabelProp="label"
                      options={[
                        {
                          value: 'sku',
                          label: t('enums.sales_channel.map_product_rule.sku'),
                          description: t('enums.sales_channel.map_product_rule.sku_description'),
                        },
                        {
                          value: 'barcode',
                          label: t('enums.sales_channel.map_product_rule.barcode'),
                          description: t('enums.sales_channel.map_product_rule.barcode_description'),
                        },
                      ]}
                      optionRender={(option) => (
                        <div>
                          <div>{option.data.label}</div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            {option.data.description}
                          </div>
                        </div>
                      )}
                    />
                  </Form.Item>
              </Col>
            </Row>
            {(mapProductRule === 'barcode' || mapProductRule === 'batch') && (
              <Row gutter={16}>
                <Col span={24}>
                  <Alert
                    message={t('shared.sales_channels.form.map_product_rule_warning.title')}
                    description={t('shared.sales_channels.form.map_product_rule_warning.message')}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                </Col>
              </Row>
            )}
        </Card>

        {/* 允收設定 */}
        <Card
          title={<Space><ClockCircleOutlined />{t('shared.sales_channels.form.sections.acceptance_settings')}</Space>}
          style={{ marginBottom: 16 }}
        >
            <div style={{ fontSize: 14, fontWeight: 500, color: '#666', marginTop: 4, marginBottom: 12 }}>
              {t('shared.sales_channels.form.inventory_settings_title')}
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="inventory_type_id"
                  label={
                    <Space>
                      {t('models.sales_channel.inventory_type')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.inventory_type')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true, message: t('common.validation.required') }]}
                >
                  <Select
                    placeholder={t('common.placeholder.please_select')}
                    allowClear
                    showSearch
                    filterOption={(input, option: any) =>
                      String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {inventoryTypes.map((type) => (
                      <Option key={type.id} value={type.id}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="secondary_inventory_type_id"
                  label={
                    <Space>
                      {t('models.sales_channel.secondary_inventory_type')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.secondary_inventory_type')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Select
                    placeholder={t('common.placeholder.please_select')}
                    allowClear
                    showSearch
                    filterOption={(input, option: any) =>
                      String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {inventoryTypes.map((type) => (
                      <Option key={type.id} value={type.id}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="prioritize_full_box_allocation"
                  label={
                    <Space>
                      {t('models.sales_channel.prioritize_full_box_allocation')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.prioritize_full_box_allocation')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="same_expiration_date"
                  label={
                    <Space>
                      {t('models.sales_channel.same_expiration_date')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.same_expiration_date')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            {/* 效期管理 */}
            <div style={{ fontSize: 14, fontWeight: 500, color: '#666', marginTop: 24, marginBottom: 12 }}>
              {t('shared.sales_channels.form.expiration_settings_title')}
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="expiration_mechanism"
                  label={
                    <Space>
                      {t('models.sales_channel.expiration_mechanism')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.expiration_mechanism')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Select onChange={handleExpirationMechanismChange}>
                    <Option value="none">{t('enums.sales_channel.expiration_mechanism.none')}</Option>
                    <Option value="by_days">{t('enums.sales_channel.expiration_mechanism.by_days')}</Option>
                    <Option value="by_ratio">{t('enums.sales_channel.expiration_mechanism.by_ratio')}</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* 預設效期設定 */}
            {expirationMechanism !== 'none' && (
              <>
                <Divider orientation="left" plain>
                  {t('shared.sales_channels.form.default_expiration_settings')}
                </Divider>
                <Row gutter={16}>
                  {expirationMechanism === 'by_days' ? (
                    <>
                      <Col span={12}>
                        <Form.Item
                          name="days_before_expiration"
                          label={
                            <Space>
                              {t('models.sales_channel.days_before_expiration')}
                              <Tooltip title={t('shared.sales_channels.form.tooltips.days_before_expiration')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder={t('common.placeholder.please_enter')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="oversea_days_before_expiration"
                          label={
                            <Space>
                              {t('models.sales_channel.oversea_days_before_expiration')}
                              <Tooltip title={t('shared.sales_channels.form.tooltips.oversea_days_before_expiration')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder={t('common.placeholder.please_enter')}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col span={12}>
                        <Form.Item
                          name="expiration_ratio"
                          label={
                            <Space>
                              {t('models.sales_channel.expiration_ratio')}
                              <Tooltip title={t('shared.sales_channels.form.tooltips.expiration_ratio')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <InputNumber<number>
                            min={0}
                            max={100}
                            style={{ width: '100%' }}
                            placeholder={t('common.placeholder.please_enter')}
                            formatter={(value) => `${value}%`}
                            parser={(value) => (value || '').replace('%', '') as unknown as number}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="oversea_expiration_ratio"
                          label={
                            <Space>
                              {t('models.sales_channel.oversea_expiration_ratio')}
                              <Tooltip title={t('shared.sales_channels.form.tooltips.oversea_expiration_ratio')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <InputNumber<number>
                            min={0}
                            max={100}
                            style={{ width: '100%' }}
                            placeholder={t('common.placeholder.please_enter')}
                            formatter={(value) => `${value}%`}
                            parser={(value) => (value || '').replace('%', '') as unknown as number}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>

                {/* 階梯式效期規則 */}
                <Divider orientation="left" plain>
                  <Space>
                    {t('shared.sales_channels.form.tiered_expiration_rules')}
                    <Tooltip title={t('shared.sales_channels.form.tiered_expiration_rules_tooltip')}>
                      <QuestionCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                </Divider>
                <div style={{ marginBottom: 16, color: '#666', fontSize: '13px' }}>
                  {t('shared.sales_channels.form.tiered_rules_hint')}
                </div>
                {rules.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Table
                      dataSource={rules}
                      rowKey={(record: any, index?: number) => record.id || index}
                      pagination={false}
                      size="middle"
                      bordered
                      columns={[
                        {
                          title: (
                            <div style={{ textAlign: 'center' }}>
                              {t('common.actions')}
                            </div>
                          ),
                          width: 70,
                          align: 'center',
                          fixed: 'left',
                          render: (_: any, __: any, index: number) => (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteRule(index)}
                              size="small"
                            />
                          ),
                        },
                        {
                          title: (
                            <Space>
                              <span>{t('shared.sales_channels.form.rule_table.range_condition')}</span>
                              <Tooltip title={t('shared.sales_channels.form.rule_table.threshold_tooltip')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          ),
                          dataIndex: 'original_expiration_date_threshold',
                          width: 280,
                          render: (value: number, _record: any, index: number) => {
                            const nextRule = rules[index + 1];
                            let rangeText = '';
                            if (nextRule && nextRule.original_expiration_date_threshold != null) {
                              rangeText = `${value || 0} ~ ${nextRule.original_expiration_date_threshold - 1}`;
                            } else {
                              rangeText = `≥ ${value || 0}`;
                            }

                            return (
                              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                                <InputNumber
                                  min={0}
                                  value={value}
                                  onChange={(val) => handleUpdateRule(index, 'original_expiration_date_threshold', val)}
                                  style={{ width: '100%' }}
                                  addonAfter={t('shared.sales_channels.form.rule_table.days_unit')}
                                  placeholder={t('common.placeholder.please_enter')}
                                />
                                <div style={{
                                  backgroundColor: '#e6f7ff',
                                  border: '1px solid #91d5ff',
                                  borderRadius: '4px',
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  color: '#0050b3',
                                  textAlign: 'center'
                                }}>
                                  <strong>{t('shared.sales_channels.form.rule_table.applicable_range')}:</strong> {rangeText} {t('shared.sales_channels.form.rule_table.days_unit')}
                                </div>
                              </Space>
                            );
                          },
                        },
                        {
                          title: (
                            <Space>
                              {t('shared.sales_channels.form.rule_table.domestic_acceptance')}
                              <Tooltip title={t('shared.sales_channels.form.rule_table.domestic_tooltip')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          ),
                          width: 200,
                          render: (_: any, record: any, index: number) => {
                            const field = expirationMechanism === 'by_days' ? 'days_before_expiration' : 'expiration_ratio';
                            return (
                              <InputNumber
                                min={0}
                                max={expirationMechanism === 'by_ratio' ? 100 : undefined}
                                value={record[field]}
                                onChange={(val) => handleUpdateRule(index, field, val)}
                                style={{ width: '100%' }}
                                formatter={expirationMechanism === 'by_ratio' ? (v) => `${v}%` : undefined}
                                parser={expirationMechanism === 'by_ratio' ? ((v?: string) => (v || '').replace('%', '') as unknown as number) : undefined}
                                addonAfter={expirationMechanism === 'by_days' ? t('shared.sales_channels.form.rule_table.days_unit') : undefined}
                              />
                            );
                          },
                        },
                        {
                          title: (
                            <Space>
                              {t('shared.sales_channels.form.rule_table.overseas_acceptance')}
                              <Tooltip title={t('shared.sales_channels.form.rule_table.overseas_tooltip')}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                              </Tooltip>
                            </Space>
                          ),
                          width: 200,
                          render: (_: any, record: any, index: number) => {
                            const field = expirationMechanism === 'by_days' ? 'oversea_days_before_expiration' : 'oversea_expiration_ratio';
                            return (
                              <InputNumber
                                min={0}
                                max={expirationMechanism === 'by_ratio' ? 100 : undefined}
                                value={record[field]}
                                onChange={(val) => handleUpdateRule(index, field, val)}
                                style={{ width: '100%' }}
                                formatter={expirationMechanism === 'by_ratio' ? (v) => `${v}%` : undefined}
                                parser={expirationMechanism === 'by_ratio' ? ((v?: string) => (v || '').replace('%', '') as unknown as number) : undefined}
                                addonAfter={expirationMechanism === 'by_days' ? t('shared.sales_channels.form.rule_table.days_unit') : undefined}
                              />
                            );
                          },
                        },
                      ]}
                    />
                  </div>
                )}
                <Button
                  type="dashed"
                  onClick={handleAddRule}
                  icon={<PlusOutlined />}
                  block
                  style={{ marginBottom: 16 }}
                >
                  {t('common.button.add')}
                </Button>
              </>
            )}

            {/* 其他效期設定 */}
            <Divider orientation="left" plain>
              {t('shared.sales_channels.form.other_expiration_settings')}
            </Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="enable_last_shipped_expiration_date_check"
                  label={
                    <Space>
                      {t('models.sales_channel.enable_last_shipped_expiration_date_check')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.enable_last_shipped_expiration_date_check')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
        </Card>

        {/* 訂單處理設定 */}
        <Card
          title={<Space><SettingOutlined />{t('shared.sales_channels.form.sections.order_settings')}</Space>}
          style={{ marginBottom: 16 }}
        >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="allow_partial_allocation"
                  label={
                    <Space>
                      {t('models.sales_channel.allow_partial_allocation')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.allow_partial_allocation')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 480 }}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Select allowClear placeholder={t('shared.sales_channels.form.placeholders.inherit_merchant')}>
                    <Select.Option value={true}>{t('shared.sales_channels.form.allow_partial_allocation_options.true')}</Select.Option>
                    <Select.Option value={false}>{t('shared.sales_channels.form.allow_partial_allocation_options.false')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fulfillment_lot_policy"
                  label={
                    <Space>
                      {t('models.sales_channel.fulfillment_lot_policy')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.fulfillment_lot_policy')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Select allowClear placeholder={t('shared.sales_channels.form.placeholders.inherit_merchant')}>
                    <Select.Option value="flexible">{t('shared.sales_channels.form.fulfillment_lot_policies.flexible')}</Select.Option>
                    <Select.Option value="strict">{t('shared.sales_channels.form.fulfillment_lot_policies.strict')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="auto_complete_fulfillment"
                  label={
                    <Space>
                      {t('models.sales_channel.auto_complete_fulfillment')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.auto_complete_fulfillment')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      {t('models.sales_channel.auto_reallocate_orders')}
                      <Tooltip title={t('shared.sales_channels.form.tooltips.auto_reallocate_orders')}>
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Space>
                    <Form.Item
                      name="auto_reallocate_orders"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch />
                    </Form.Item>
                    {autoRecheckStock && (
                      <Space>
                        <Form.Item
                          name="auto_reallocate_orders_priority"
                          noStyle
                        >
                          <InputNumber
                            min={0}
                            style={{ width: 180 }}
                            placeholder={t('common.placeholder.please_enter')}
                            addonBefore={t('models.sales_channel.priority')}
                          />
                        </Form.Item>
                        <Tooltip title={t('shared.sales_channels.form.tooltips.auto_reallocate_orders_priority')}>
                          <QuestionCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                      </Space>
                    )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
        </Card>

        {/* 物流規則設定 */}
        <Card
          title={<Space><TruckOutlined />{t('shared.sales_channels.form.sections.shipping_rules')}</Space>}
          style={{ marginBottom: 16 }}
        >
            <div style={{ fontSize: 14, fontWeight: 500, color: '#666', marginBottom: 12 }}>
              <Space>
                {t('shared.sales_channels.form.shipping_rules_title')}
                <Tooltip title={t('shared.sales_channels.form.shipping_rules_tooltip')}>
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            </div>

            <Button
              type="dashed"
              onClick={handleAddShippingRule}
              icon={<PlusOutlined />}
              style={{ marginBottom: 16 }}
            >
              {t('shared.sales_channels.form.add_shipping_rule')}
            </Button>

            {shippingRules.length > 0 && (
              <Table
                dataSource={shippingRules}
                rowKey={(record: any, index?: number) => record.id || index}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: t('shared.sales_channels.form.shipping_rule_table.original_shipping'),
                    dataIndex: 'original_shipping_type_id',
                    width: '20%',
                    render: (value: string, _: any, index: number) => (
                      <Select
                        value={value}
                        onChange={(val) => handleUpdateShippingRule(index, 'original_shipping_type_id', val)}
                        style={{ width: '100%' }}
                        placeholder={t('common.placeholder.please_select')}
                      >
                        {shippingTypes.map((type) => (
                          <Option key={type.id} value={type.id}>
                            {t(`shipping_types.${type.code}`, { defaultValue: type.name })}
                          </Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: (
                      <Space>
                        {t('shared.sales_channels.form.shipping_rule_table.condition')}
                        <Tooltip title={t('shared.sales_channels.form.shipping_rule_table.condition_tooltip')}>
                          <QuestionCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                      </Space>
                    ),
                    width: '45%',
                    render: (_: any, record: any, index: number) => (
                      <Space>
                        <Select
                          value={record.condition}
                          onChange={(val) => handleUpdateShippingRule(index, 'condition', val)}
                          style={{ width: 150 }}
                        >
                          <Option value="volume">{t('shared.sales_channels.form.conditions.volume')}</Option>
                          <Option value="weight">{t('shared.sales_channels.form.conditions.weight_kg')}</Option>
                          <Option value="postal_code">{t('shared.sales_channels.form.conditions.postal_code')}</Option>
                        </Select>

                        {['volume', 'weight'].includes(record.condition) ? (
                          <>
                            <Select
                              value={Object.keys(record.criteria || {})[0]}
                              onChange={(val) => handleUpdateShippingRule(index, 'operator', val)}
                              style={{ width: 100 }}
                            >
                              <Option value="bigger_than">{t('common.comparison.bigger_than')}</Option>
                              <Option value="smaller_than">{t('common.comparison.smaller_than')}</Option>
                            </Select>
                            <InputNumber
                              value={Object.values(record.criteria || {})[0] as number}
                              onChange={(val) => handleUpdateShippingRule(index, 'operand', val)}
                              min={0}
                              style={{ width: 100 }}
                              placeholder={t('common.placeholder.please_enter')}
                            />
                          </>
                        ) : (
                          <Select
                            mode="tags"
                            value={record.criteria?.contains || []}
                            onChange={(val) => handleUpdateShippingRule(index, 'operand', val)}
                            style={{ width: 250 }}
                            placeholder={t('shared.sales_channels.form.postal_code_placeholder')}
                            tokenSeparators={[',', ' ', '\n']}
                          />
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: t('shared.sales_channels.form.shipping_rule_table.target_shipping'),
                    dataIndex: 'shipping_type_id',
                    width: '20%',
                    render: (value: string, _: any, index: number) => (
                      <Select
                        value={value}
                        onChange={(val) => handleUpdateShippingRule(index, 'shipping_type_id', val)}
                        style={{ width: '100%' }}
                        placeholder={t('common.placeholder.please_select')}
                      >
                        {shippingTypes.map((type) => (
                          <Option key={type.id} value={type.id}>
                            {t(`shipping_types.${type.code}`, { defaultValue: type.name })}
                          </Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: t('common.actions'),
                    width: '15%',
                    render: (_: any, __: any, index: number) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteShippingRule(index)}
                      >
                        {t('common.button.delete')}
                      </Button>
                    ),
                  },
                ]}
              />
            )}
        </Card>

        {/* 計費設定 - 僅 Operator 可編輯 */}
        {role === 'operator' && (
          <Card
            title={<Space><DollarOutlined />{t('shared.sales_channels.form.sections.billing_settings')}</Space>}
            style={{ marginBottom: 16 }}
          >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="check_fee"
                    label={t('models.sales_channel.check_fee')}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      placeholder={t('common.placeholder.please_enter')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="picking_fee_per_item"
                    label={t('models.sales_channel.picking_fee_per_item')}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      placeholder={t('common.placeholder.please_enter')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="picking_fee_box"
                    label={t('models.sales_channel.picking_fee_box')}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      placeholder={t('common.placeholder.please_enter')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="picking_fee_pallet"
                    label={t('models.sales_channel.picking_fee_pallet')}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      placeholder={t('common.placeholder.please_enter')}
                    />
                  </Form.Item>
                </Col>
              </Row>
          </Card>
        )}

        <FloatingActionBarSpacer />
      </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={submitting}
        disabled={savingAndAdding}
        extraButtons={isNew ? (
          <Button
            loading={savingAndAdding}
            disabled={submitting}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
        submitRef={saveRef}
      />

      <SalesChannelFormConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </>
  );
};

export default SalesChannelsEdit;
